import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Kendi Collections — Google Analytics 4 (GA4) integration
 *
 * • Loads the gtag script exactly ONCE per session (guarded by DOM id).
 * • Sends explicit `page_view` on each SPA route change via react-router.
 * • Exposes global click helpers wired into brand-critical CTAs:
 *      window.trackEtsyClick(section, extra)
 *      window.trackDonation(provider, extra)
 *      window.trackPartner(partnerName, url, extra)
 *      window.trackNewsletterSignup(source)
 *      window.trackNavigation(label, destination, isExternal)
 * • Automatically no-ops when REACT_APP_GA_MEASUREMENT_ID is empty
 *   (e.g. local dev without the ID).
 */
export default function Analytics() {
  const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const pixelId = process.env.REACT_APP_META_PIXEL_ID;
  const location = useLocation();
  const initialisedRef = useRef(false);

  // --- Load gtag.js exactly once ---
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;

    // Global tracker helpers — always defined so callers never crash,
    // even when GA is not configured yet.
    const safeGtag = (...args) => {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag(...args);
      }
    };

    window.trackEtsyClick = (section = "unknown", extra = {}) => {
      safeGtag("event", "click_etsy", {
        section,
        page_path: window.location.pathname,
        destination_url: "https://kendicollections.etsy.com",
        ...extra,
      });
      if (window.fbq) window.fbq("trackCustom", "EtsyClick", { section, ...extra });
    };

    window.trackDonation = (provider = "paystack", extra = {}) => {
      safeGtag("event", "click_donation", {
        page_path: window.location.pathname,
        donation_provider: provider,
        destination_url: "https://paystack.shop/pay/55y140q79p",
        ...extra,
      });
      if (window.fbq) window.fbq("trackCustom", "DonationClick", { provider, ...extra });
    };

    window.trackPartner = (partnerName, destinationUrl, extra = {}) => {
      safeGtag("event", "click_partner", {
        partner_name: partnerName,
        destination_url: destinationUrl,
        page_path: window.location.pathname,
        ...extra,
      });
      if (window.fbq) window.fbq("trackCustom", "PartnerClick", { partner: partnerName, ...extra });
    };

    window.trackNewsletterSignup = (source = "landing_page", extra = {}) => {
      safeGtag("event", "newsletter_signup", {
        page_path: window.location.pathname,
        source,
        ...extra,
      });
      if (window.fbq) window.fbq("track", "Lead", { content_name: "newsletter", source, ...extra });
    };

    window.trackNavigation = (label, destination, isExternal = false) => {
      safeGtag("event", "navigation_click", {
        page_path: window.location.pathname,
        label,
        destination,
        is_external: !!isExternal,
      });
    };

    window.trackMoire = (sourceSection = "unknown", cta = "unknown", extra = {}) => {
      safeGtag("event", "moire_outbound_click", {
        source_page: window.location.pathname,
        source_section: sourceSection,
        cta,
        destination_url: "https://moire.live",
        ...extra,
      });
      if (window.fbq) window.fbq("trackCustom", "MoireOutbound", { section: sourceSection, cta, ...extra });
    };

    // Bail out if there's no GA ID configured (dev / preview without key)
    if (!gaId) return;

    // Guard against duplicate script injection (idempotent)
    if (document.getElementById("ga4-script")) return;

    // 1. Load gtag.js async
    const s = document.createElement("script");
    s.id = "ga4-script";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(s);

    // 2. Initialise dataLayer + gtag stub
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag("js", new Date());

    // Config with send_page_view: false — we send manually on route change
    // to avoid duplicate PVs in SPA context.
    gtag("config", gaId, {
      send_page_view: false,
      anonymize_ip: true,
      cookie_flags: "SameSite=None;Secure",
    });

    // Optional: Meta Pixel (only loads if ID configured)
    if (pixelId && !window.fbq) {
      /* eslint-disable */
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      window.fbq("init", pixelId);
      /* eslint-enable */
    }
  }, [gaId, pixelId]);

  // --- Send page_view on every SPA route change ---
  useEffect(() => {
    if (!gaId) return;
    if (typeof window.gtag !== "function") return;

    const path = location.pathname + location.search;
    // Small delay so document.title reflects the new route's useSEO update
    const t = setTimeout(() => {
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
      if (window.fbq) window.fbq("track", "PageView");
    }, 60);

    return () => clearTimeout(t);
  }, [location.pathname, location.search, gaId]);

  return null;
}
